import Route from '@ember/routing/route';
import type Transition from '@ember/routing/transition';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import type MapFloorModel from 'garaje/models/map-floor';
import type StateService from 'garaje/services/state';

import type SpacesMapsLiveNewController from './controller';

export interface ModelResponse {
  mapFloors: MapFloorModel[];
}

/**
 * Loads the floors for the current location — the only reason the
 * route still fetches anything is to validate the `selectedFloorId`
 * queryParam against the real floors before the iframe URL is built.
 * The map itself renders inside the floor-plan-fe iframe, which loads
 * its own data from floor-plan-service.
 *
 * Neighborhoods + workplaceDays are no longer loaded here — the FE
 * gets neighborhoods from the render envelope's layer manifest, and
 * workday-aware date validation is deferred to a follow-up (see the
 * toolbar-migration plan in floor-plan-fe).
 */
export default class SpacesMapsLiveNewRoute extends Route {
  @service declare store: Store;
  @service declare state: StateService;

  async model(): Promise<ModelResponse> {
    const locationId = this.state.currentLocation?.id;
    if (!locationId) return { mapFloors: [] };
    const mapFloors = await this.store.query('map-floor', { filter: { location: locationId } });
    return { mapFloors: mapFloors.slice() };
  }

  /**
   * Reconcile a bookmarked `selectedFloorId` against the floors
   * actually loaded. If the persisted id doesn't match a real floor
   * (renamed, deleted, wrong location), clear it so the URL and the
   * iframe src stay in agreement.
   */
  setupController(controller: SpacesMapsLiveNewController, model: ModelResponse, transition: Transition): void {
    super.setupController(controller, model, transition);
    const persisted = controller.selectedFloorId;
    if (persisted && !model.mapFloors.some((f) => f.id === persisted)) {
      controller.selectedFloorId = null;
    }
  }
}
